import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const ORDER_STATUSES = ['created', 'paid', 'processing', 'shipped', 'delivered', 'cancelled'];

export const AdminOrders = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/admin/orders`, { withCredentials: true })
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/api/admin/orders/${orderId}/status`, { status }, { withCredentials: true });
      toast({ title: `Order updated to "${status}"` });
      load();
    } catch (e) {
      toast({ title: 'Failed to update order status', variant: 'destructive' });
    }
  };

  if (orders === null) return <p className="text-gray-500 py-8">Loading orders…</p>;
  if (orders.length === 0) return <p className="text-gray-500 py-8" data-testid="admin-no-orders">No orders yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="admin-orders-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-3 pr-4 font-medium">Order ID</th>
            <th className="py-3 pr-4 font-medium">Date</th>
            <th className="py-3 pr-4 font-medium">Customer</th>
            <th className="py-3 pr-4 font-medium">Items</th>
            <th className="py-3 pr-4 font-medium">Amount</th>
            <th className="py-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.order_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-order-row-${order.order_id}`}>
              <td className="py-3 pr-4 font-mono text-xs text-gray-700">{order.order_id}</td>
              <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
              <td className="py-3 pr-4">
                <p className="text-gray-900">{order.customer_name || '—'}</p>
                <p className="text-gray-500 text-xs">{order.customer_email || ''}</p>
              </td>
              <td className="py-3 pr-4 text-gray-600 max-w-[220px]">
                {order.items?.map((i) => `${i.name} ×${i.quantity}`).join(', ')}
              </td>
              <td className="py-3 pr-4 font-semibold text-gray-900 whitespace-nowrap">₹{order.amount?.toLocaleString('en-IN')}</td>
              <td className="py-3">
                <select
                  value={order.status}
                  onChange={(e) => updateStatus(order.order_id, e.target.value)}
                  data-testid={`admin-order-status-select-${order.order_id}`}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
